import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { SessionEndModal } from 'src/components/modals';
import { makeLogout } from 'src/utils/auth';
import { useTokenRefresh } from './useTokenRefresh';
import DebugPanel from './DebugPanel';
import {
  DEBUG_MODE,
  TEST_MODE,
  TIME_UNIT,
  INACTIVITY_LOGOUT_TIME,
  ACTIVITY_CHECK_INTERVAL,
  WARNING_THRESHOLD,
  ACTIVITY_EVENTS,
  log,
} from './types';

const LOG_CTX = 'Inactivity';

type WarningReason = 'inactivity' | 'tokenFailure' | null;

/**
 * SessionManager - Handles:
 * 1. Proactive token refresh before expiry
 * 2. Inactivity timeout with warnings
 * 3. Token refresh failure warnings
 * 4. Optional debug panel for development
 */
const SessionManager: React.FC = () => {
  const { logout: logoutAuth0, isAuthenticated } = useAuth0();

  const { tokenInfo, refreshCount, lastRefresh, refreshLog, error, isRefreshing, forceRefresh } = useTokenRefresh();

  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [warningReason, setWarningReason] = useState<WarningReason>(null);
  const [minutesRemaining, setMinutesRemaining] = useState(WARNING_THRESHOLD);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const tokenFailureShownRef = useRef(false);
  const isWarningOpenRef = useRef(isWarningOpen);
  const warningReasonRef = useRef(warningReason);
  isWarningOpenRef.current = isWarningOpen;
  warningReasonRef.current = warningReason;

  const updateActivity = useCallback(() => {
    if (isWarningOpenRef.current) return;
    const now = Date.now();
    lastActivityRef.current = now;
    setLastActivity(now);
  }, []);

  const performLogout = useCallback(() => {
    makeLogout({ noEvents: true });
    logoutAuth0({
      logoutParams: { returnTo: `${window.location.protocol}//${window.location.host}/auth` },
    });
  }, [logoutAuth0]);

  const checkInactivity = useCallback(() => {
    if (!isAuthenticated) return;

    const now = Date.now();
    const timeSinceActivity = now - lastActivityRef.current;
    const timeUntilLogout = INACTIVITY_LOGOUT_TIME - timeSinceActivity;
    const unitsLeft = Math.max(0, Math.ceil(timeUntilLogout / TIME_UNIT));
    const unit = TEST_MODE ? 's' : 'm';

    log(LOG_CTX, `🔍 Idle: ${Math.round(timeSinceActivity / TIME_UNIT)}${unit}, remaining: ${unitsLeft}${unit}`);

    if (timeSinceActivity >= INACTIVITY_LOGOUT_TIME) {
      log(LOG_CTX, '⏰ Timeout reached, logging out...');
      performLogout();
      return;
    }

    if (unitsLeft <= WARNING_THRESHOLD && !warningShownRef.current) {
      warningShownRef.current = true;
      setMinutesRemaining(unitsLeft);
      setWarningReason('inactivity');
      setIsWarningOpen(true);
      log(LOG_CTX, `⚠️ Inactivity warning shown (${unitsLeft}${unit} remaining)`);
    } else if (isWarningOpenRef.current && warningReasonRef.current === 'inactivity') {
      setMinutesRemaining(unitsLeft);
    }
  }, [isAuthenticated, performLogout]);

  useEffect(() => {
    if (!isAuthenticated) return;

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [isAuthenticated, updateActivity]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const intervalId = setInterval(checkInactivity, ACTIVITY_CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, checkInactivity]);

  useEffect(() => {
    if (!error || tokenFailureShownRef.current) return;

    const { accessExp, idExp } = tokenInfo;
    const earliestExp = Math.min(accessExp || Infinity, idExp || Infinity);

    if (earliestExp === Infinity) {
      log(LOG_CTX, '❌ Token refresh failed but no expiry info available');
      return;
    }

    const timeUntilExpiry = earliestExp - Date.now();
    const unitsLeft = Math.max(0, Math.ceil(timeUntilExpiry / TIME_UNIT));

    tokenFailureShownRef.current = true;
    setMinutesRemaining(unitsLeft);
    setWarningReason('tokenFailure');
    setIsWarningOpen(true);
    log(LOG_CTX, `⚠️ Token refresh failed, warning shown (${unitsLeft} remaining until expiry)`);
  }, [error, tokenInfo]);

  useEffect(() => {
    if (!error) return;

    const { accessExp, idExp } = tokenInfo;
    const earliestExp = Math.min(accessExp || Infinity, idExp || Infinity);

    if (earliestExp === Infinity) return;

    const checkTokenExpiry = () => {
      const timeUntilExpiry = earliestExp - Date.now();

      if (timeUntilExpiry <= 0) {
        log(LOG_CTX, '⏰ Token expired after refresh failure, logging out...');
        performLogout();
        return;
      }

      if (isWarningOpenRef.current && warningReasonRef.current === 'tokenFailure') {
        const unitsLeft = Math.max(0, Math.ceil(timeUntilExpiry / TIME_UNIT));
        setMinutesRemaining(unitsLeft);
      }
    };

    checkTokenExpiry();

    const intervalId = setInterval(checkTokenExpiry, ACTIVITY_CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [error, tokenInfo, performLogout]);

  const handleCloseWarning = () => {
    if (warningReason === 'inactivity') {
      const now = Date.now();
      lastActivityRef.current = now;
      setLastActivity(now);
      warningShownRef.current = false;
      log(LOG_CTX, '✅ Inactivity warning acknowledged');
    } else if (warningReason === 'tokenFailure') {
      tokenFailureShownRef.current = false;
      log(LOG_CTX, '✅ Token failure warning acknowledged');
    }
    setWarningReason(null);
    setIsWarningOpen(false);
  };

  const expirationTime = new Date(Date.now() + minutesRemaining * TIME_UNIT)
    .toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      second: TEST_MODE ? '2-digit' : undefined,
      hour12: true,
    })
    .toUpperCase();

  return (
    <>
      {isWarningOpen && warningReason && (
        <SessionEndModal
          onClose={handleCloseWarning}
          minutesRemaining={minutesRemaining}
          expirationTime={expirationTime}
          reason={warningReason}
        />
      )}

      {DEBUG_MODE && (
        <DebugPanel
          tokenInfo={tokenInfo}
          refreshCount={refreshCount}
          lastRefresh={lastRefresh}
          refreshLog={refreshLog}
          error={error}
          isRefreshing={isRefreshing}
          onForceRefresh={forceRefresh}
          lastActivity={lastActivity}
        />
      )}
    </>
  );
};

export default SessionManager;
